import { AppContext } from "@/services/AppContext/appContext";
import { Card, CardContent } from "@/shadcn/components/ui/card";
import { ChatMessage } from "@/types";
import { exportSingleMessageToTxtFile } from "@/utils/Export/exportSingeMessage.util";
import { copyToClipboard } from "@/utils/utils.util";
import hljs from "highlight.js/lib/common";
import bash from "highlight.js/lib/languages/bash";
import javascript from "highlight.js/lib/languages/javascript"; // Import JavaScript language
import typescript from "highlight.js/lib/languages/typescript"; // Import TypeScript language
import { Clipboard, FileDown } from "lucide-react";
import MarkdownIt from "markdown-it";
import React, { useContext, useEffect, useRef } from "react";

// Register bash language for highlight.js so markdown-it can recognize it
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("typescriptreact", typescript);
hljs.registerLanguage("javascriptreact", javascript);

// Configure markdown-it to output code blocks with classes for highlight.js
let codeBlockIdCounter = 0;

const md: MarkdownIt = new MarkdownIt({
  breaks: true,
  highlight: (str: string, lang: string): string => {
    // This function now only returns the highlighted HTML string for the code itself.
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(str, {
          language: lang,
          ignoreIllegals: true,
        }).value;
      } catch {
        // Fallback if highlighting fails
      }
    }
    // Fallback for no language or highlighting error
    return md.utils.escapeHtml(str);
  },
});

md.renderer.rules.fence = function (tokens, idx, options, env, self) {
  const token = tokens[idx];
  const lang = token.info ? token.info.trim() : "";
  const codeId = `code-block-${codeBlockIdCounter++}`;

  // Get the raw content of the code block
  const rawCode = token.content;

  // Use the highlight function to get the highlighted HTML
  const attrs = token.info || ""; // The third argument for the highlight function
  const highlightedCode = md.options.highlight
    ? md.options.highlight(rawCode, lang, attrs)
    : md.utils.escapeHtml(rawCode);

  return `
    <div class="code-block-wrapper prose" data-code-id="${codeId}">
      <pre><code class="language-${lang || "none"} hljs">${highlightedCode}</code></pre>
      <button class="copy-code-button" data-code-id="${codeId}" title="Copy code">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clipboard-icon lucide-clipboard"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
      </button>
    </div>
  `;
};

md.renderer.rules.code_block = function (tokens, idx, options, env, self) {
  const token = tokens[idx];
  // Indented code blocks typically don't have language info in token.info
  const lang = "";
  const codeId = `code-block-${codeBlockIdCounter++}`;

  // Get the raw content of the code block
  const rawCode = token.content;

  // Use the highlight function to get the highlighted HTML
  const attrs = ""; // No attributes for indented blocks
  const highlightedCode = md.options.highlight
    ? md.options.highlight(rawCode, lang, attrs)
    : md.utils.escapeHtml(rawCode);

  return `
    <div class="code-block-wrapper prose" data-code-id="${codeId}">
      <pre><code class="hljs">${highlightedCode}</code></pre>
      <button class="copy-code-button" data-code-id="${codeId}" title="Copy code">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clipboard-icon lucide-clipboard"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
      </button>
    </div>
  `;
};

// Custom paragraph_open renderer to insert <br> for extra blank lines between paragraphs
const defaultParagraphRenderer =
  md.renderer.rules.paragraph_open ||
  ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
md.renderer.rules.paragraph_open = function (tokens, idx, options, env, self) {
  let result = "";
  if (idx > 1) {
    const inline = tokens[idx - 2];
    const paragraph = tokens[idx];
    if (
      inline.type === "inline" &&
      inline.map &&
      inline.map[1] &&
      paragraph.map &&
      paragraph.map[0]
    ) {
      const diff = paragraph.map[0] - inline.map[1];
      if (diff > 0) {
        result = "<br>".repeat(diff);
      }
    }
  }
  return result + defaultParagraphRenderer(tokens, idx, options, env, self);
};

// Custom table_open renderer to wrap tables in a scrollable div
md.renderer.rules.table_open = function (tokens, idx, options, env, self) {
  return '<div class="table-scroll">' + self.renderToken(tokens, idx, options);
};

// Custom table_close renderer to close the scrollable div
md.renderer.rules.table_close = function (tokens, idx, options, env, self) {
  return self.renderToken(tokens, idx, options) + "</div>";
};

// open links in new tab
md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  const aIndex = tokens[idx].attrIndex("target");
  if (aIndex < 0) {
    tokens[idx].attrPush(["target", "_blank"]); // add new attribute
  } else {
    // Ensures attrs is not null before assignment to prevent runtime errors
    if (tokens[idx].attrs) {
      tokens[idx].attrs[aIndex][1] = "_blank";
    }
  }
  return self.renderToken(tokens, idx, options);
};

type ChatMessageCardProps = {
  message: ChatMessage;
};

const ChatMessageCard: React.FC<ChatMessageCardProps> = ({ message }) => {
  const { selectedAgentModel } = useContext(AppContext);
  const messageContentRef = useRef<HTMLDivElement>(null);

  const htmlContent = md.render(message.content);

  useEffect(() => {
    const messageContentElement = messageContentRef.current;
    if (!messageContentElement) return;

    const handleCopyCode = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest("button.copy-code-button");
      if (!button) return;

      const codeId = button.getAttribute("data-code-id");
      if (!codeId) return;

      const codeWrapper = messageContentElement.querySelector(
        `div.code-block-wrapper[data-code-id="${codeId}"]`
      );
      if (!codeWrapper) return;

      const codeElement = codeWrapper.querySelector("code");
      if (!codeElement) return;

      const codeToCopy = codeElement.innerText || codeElement.textContent;
      if (codeToCopy) {
        copyToClipboard(codeToCopy);
      }
    };

    const copyButtons = messageContentElement.querySelectorAll(
      "button.copy-code-button"
    );
    copyButtons.forEach((button) => {
      button.addEventListener("click", handleCopyCode);
    });

    return () => {
      copyButtons.forEach((button) => {
        button.removeEventListener("click", handleCopyCode);
      });
    };
  }, [htmlContent]); // Rerun effect when htmlContent changes

  return (
    <div
      className={`flex max-w-full ${
        message.sender === "account"
          ? "mr-0 ml-auto justify-end"
          : "justify-start"
      }`}
    >
      <div className="">
        <Card
          className={`max-w-[80%] min-w-60 py-0 ${
            message.sender === "account"
              ? "mb-2"
              : "!border-none bg-transparent !shadow-none !outline-none"
          }`}
        >
          <CardContent
            className={`grid max-w-full ${
              message.sender === "account" ? "p-3" : "p-0"
            }`}
          >
            {message.sender === "agent" && (
              <div className="mb-0 flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {selectedAgentModel?.name || "Agent"}
                </span>
              </div>
            )}
            <div
              ref={messageContentRef}
              className="ProseMirror prose messageContent max-w-full overflow-x-hidden"
              style={{
                wordWrap: "break-word",
                // whiteSpace: "pre-wrap",
                // whiteSpace: "break-spaces",
                WebkitFontVariantLigatures: "none",
                fontVariantLigatures: "none",
                fontFeatureSettings: '"liga" 0',
              }}
              // Use dangerouslySetInnerHTML to render HTML output from markdown-it
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </CardContent>
        </Card>
        <div className="flex">
          <Clipboard
            className="m-1 cursor-pointer text-gray-500 hover:text-gray-700"
            onClick={() => copyToClipboard(message.content)}
            size={18}
          />
          {/* txt export */}
          <FileDown
            className="m-1 cursor-pointer text-gray-500 hover:text-gray-700"
            onClick={() => exportSingleMessageToTxtFile(message)}
            size={18}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatMessageCard;
